"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, PhoneCall, Clock, ShieldCheck, Navigation, CircleCheck as CheckCircle2, ArrowRight, Truck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
const primaryZones = [{
  name: "Downtown Jonesboro & Historic District",
  zipCodes: "30236, 30237",
  estArrival: "15-25 min",
  features: "24/7 Rapid Dispatch, Residential & Commercial"
}, {
  name: "Tara Boulevard Corridor",
  zipCodes: "30236, 30238",
  estArrival: "15-20 min",
  features: "Emergency Main Leak & Drain Clearing Units"
}, {
  name: "Irondale & North Clayton",
  zipCodes: "30238, 30297",
  estArrival: "20-30 min",
  features: "Sewer Backup, Water Heater Emergency"
}, {
  name: "Lake Spivey & South Clayton",
  zipCodes: "30236, 30273",
  estArrival: "20-30 min",
  features: "Burst Pipe & Flooding Rapid Response"
}];
const nearbyAreas = ["Morrow, GA", "Riverdale, GA", "Forest Park, GA", "Stockbridge, GA", "Lovejoy, GA", "Fayetteville, GA", "Rex, GA", "Hampton, GA"];
const serviceHighlights = [{
  title: "Always On Call",
  description: "Fully staffed dispatch team active 24 hours a day, 365 days a year across Clayton County."
}, {
  title: "Equipped Mobile Units",
  description: "Our vans arrive stocked with industrial pumps, pipe fittings, and detection gear."
}, {
  title: "No Hidden Travel Surcharges",
  description: "Upfront transparent pricing before any repair begins, day or night."
}];
export default function ServiceArea() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="servicearea" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-24 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            <MapPin className="size-3.5 mr-1 text-accent" />
            Clayton County Coverage
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Fast Emergency Plumbing Across Jonesboro, GA
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            When burst pipes, overflowing drains, or broken water heaters strike, local response time matters. Our licensed plumbers are stationed strategically throughout Jonesboro and neighboring communities for rapid dispatch.
          </p>
        </div>

        {/* Main Grid: Coverage Zones & Interactive Overview */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Primary Zones Cards */}
          <div className="lg:col-span-7 flex flex-col gap-4">
            <h3 className="text-xl font-default font-semibold text-foreground flex items-center gap-2">
              <Truck className="size-5 text-primary" />
              Direct Emergency Dispatch Hubs
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {primaryZones.map((zone, i) => <motion.div key={zone.name} variants={itemVariants} data-index={i}>
                  <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs font-semibold text-primary uppercase tracking-wide flex items-center gap-1">
                          <Navigation className="size-3" />
                          {zone.zipCodes}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs font-medium bg-muted text-muted-foreground px-2 py-0.5 rounded-md">
                          <Clock className="size-3 text-accent" />
                          {zone.estArrival}
                        </span>
                      </div>
                      <CardTitle className="text-base font-default text-card-foreground">
                        {zone.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-xs sm:text-sm text-muted-foreground font-sans">
                        {zone.features}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>)}
            </div>

            {/* Additional Surrounding Towns */}
            <div className="mt-4 p-5 rounded-xl bg-secondary/50 border border-border">
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary" />
                Also Serving Adjacent Communities in Clayton & Henry Counties:
              </h4>
              <div className="flex flex-wrap gap-2">
                {nearbyAreas.map((area, i) => <span key={area} data-index={i} className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-background text-foreground border border-border">
                    {area}
                  </span>)}
              </div>
            </div>
          </div>

          {/* Right Column: Dispatch Highlights & Call To Action */}
          <motion.div variants={itemVariants} className="lg:col-span-5 flex flex-col gap-6">
            <Card className="bg-primary text-primary-foreground border-transparent shadow-lg relative overflow-hidden">
              <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
                <ShieldCheck className="size-48" />
              </div>
              
              <CardHeader className="relative z-10 pb-4">
                <Badge variant="secondary" className="w-fit bg-accent text-accent-foreground font-semibold mb-2">
                  24/7 Immediate Help
                </Badge>
                <CardTitle className="text-2xl font-default text-primary-foreground font-bold">
                  Facing a Plumbing Emergency in Jonesboro?
                </CardTitle>
                <CardDescription className="text-primary-foreground/80 text-sm mt-1">
                  Don't wait for water damage to escalate. Talk directly to a licensed local technician right now.
                </CardDescription>
              </CardHeader>

              <CardContent className="relative z-10 space-y-4 pt-0">
                <div className="space-y-3 pt-2">
                  {serviceHighlights.map((highlight, i) => <div key={highlight.title} data-index={i} className="flex items-start gap-3">
                      <div className="size-5 rounded-full bg-primary-foreground/15 flex items-center justify-center shrink-0 mt-0.5">
                        <CheckCircle2 className="size-3.5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-primary-foreground">{highlight.title}</p>
                        <p className="text-xs text-primary-foreground/75 leading-relaxed">{highlight.description}</p>
                      </div>
                    </div>)}
                </div>

                <div className="pt-4 flex flex-col sm:flex-row gap-3">
                  <Button asChild size="lg" className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-md">
                    <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                      <PhoneCall className="size-4" />
                      Call Emergency Dispatch
                    </a>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="w-full bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20 hover:bg-primary-foreground/20">
                    <Link to="/contact/" className="flex items-center justify-center gap-2">
                      <span>Online Request</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Sub-info banner */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-card border border-border text-xs text-muted-foreground">
              <span className="flex items-center gap-2">
                <ShieldCheck className="size-4 text-primary" />
                Licensed & Insured in Georgia
              </span>
              <span>Available 24 Hours / 7 Days</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}